// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { HasCartContext, withCart } from "@insite/client-framework/Components/CartContext";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import OrderApprovalDetailsActionButtons, {
    OrderApprovalDetailsActionButtonsStyles,
} from "@insite/content-library/Widgets/OrderApprovalDetails/OrderApprovalDetailsActionButtons";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import React, { FC } from "react";
import { css } from "styled-components";

// XNG-Changes: Add new Fields
const enum fields {
    show = "show",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.show]: string;
    };
}

export interface OrderApprovalDetailsHeaderStyles {
    headerGridContainer?: GridContainerProps;
    title?: TypographyProps;
    orderNumberText?: TypographyProps;
    titleGridItem?: GridItemProps;
    buttonGridItem?: GridItemProps;
    orderApprovalDetailsActionButtonsStyles?: OrderApprovalDetailsActionButtonsStyles;
}

export const headerStyles: OrderApprovalDetailsHeaderStyles = {
    buttonGridItem: {
        css: css`
            justify-content: flex-end;
        `,
        width: [2, 2, 2, 6, 6],
    },
    titleGridItem: {
        width: [10, 10, 10, 6, 6],
    },
    title: {
        variant: "h2",
        as: "p",
        css: css`
            @media print {
                font-size: 11px;
            }
        `,
    },
};

const styles = headerStyles;

type Props = OwnProps & HasCartContext;

const OrderApprovalDetailsHeader = ({ cart, fields: { show } }: Props) => {
    if (!cart) {
        return null;
    }

    return (
        <GridContainer {...styles.headerGridContainer}>
            {(show === "title" || show === "both") && (
                <GridItem {...styles.titleGridItem}>
                    <Typography {...styles.title}>
                        <span aria-hidden>{translate("Order #")}&nbsp;</span>
                        <VisuallyHidden>{translate("Order Number")}</VisuallyHidden>
                        <Typography {...styles.orderNumberText} data-test-selector="orderApprovalDetails_orderNumber">
                            {cart.orderNumber}
                        </Typography>
                    </Typography>
                </GridItem>
            )}
            {(show === "actions" || show === "both") && (
                <GridItem {...styles.buttonGridItem}>
                    <OrderApprovalDetailsActionButtons
                        cart={cart}
                        extendedStyles={styles.orderApprovalDetailsActionButtonsStyles}
                    />
                </GridItem>
            )}
        </GridContainer>
    );
};

const widgetModule: WidgetModule = {
    component: withCart(OrderApprovalDetailsHeader),
    definition: {
        displayName: "Page Header",
        allowedContexts: ["OrderApprovalDetailsPage"],
        group: "Order Approval Details",
        fieldDefinitions: [
            {
                name: "show",
                displayName: "Show",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Title", value: "title" },
                    { displayName: "Actions", value: "actions" },
                    { displayName: "Both", value: "both" },
                ],
                fieldType: "General",
                defaultValue: "title",
            },
        ],
    },
};

export default widgetModule;
